// client/src/components/pages/AmendmentsAllocateTransactions/AmendmentTimeline.tsx (converted from .js, ST-00216)
import { useState, useEffect } from 'react';
import {
  FaSpinner,
  FaClock,
  FaFilePdf,
  FaFile,
  FaSearchPlus,
  FaUserEdit,
  FaStar,
  FaHourglassHalf,
} from 'react-icons/fa';
import api from '../../../services/api';
import type {
  ApiErrorBody,
  TimelineAttachment,
  TimelineEntry,
  TimelineResponse,
} from '../../../types/clientPages/amendments';

/** What the timeline reads from a rejected request. */
type RequestError = Error & { response?: { data?: ApiErrorBody } };

interface AmendmentTimelineProps {
  ticketUID: string | null;
  onPreviewAttachment?: (attachment: TimelineAttachment) => void;
}

const formatDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleString();
};

const DOT_COLORS: Record<string, string> = {
  original: 'bg-primary-500',
  amendment: 'bg-primary-500',
  pending_approval: 'bg-amber-400',
};

const isPreviewable = (mime: string | null | undefined) =>
  mime?.startsWith('image/') || mime === 'application/pdf';

const AmendmentTimeline = ({ ticketUID, onPreviewAttachment }: AmendmentTimelineProps) => {
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ticketUID) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.amendmentsV3
      .getTimeline(ticketUID)
      .then((res) => {
        if (!cancelled) setTimeline((res.data as TimelineResponse).timeline || []);
      })
      .catch((err: RequestError) => {
        if (!cancelled) setError(err.response?.data?.message || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ticketUID]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <FaSpinner className="w-5 h-5 animate-spin mr-2" /> Loading timeline…
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-red-600 py-4">{error}</div>;
  }

  if (timeline.length === 0) {
    return (
      <div className="text-sm text-gray-400 text-center py-8">
        No history found for this ticket.
      </div>
    );
  }

  return (
    <div className="relative pl-6">
      {/* Vertical line */}
      <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-gray-200" />

      <div className="space-y-6">
        {timeline.map((entry, idx) => (
          <div key={idx} className="relative">
            {/* Dot */}
            <div
              className={`absolute -left-6 top-1 w-[22px] h-[22px] rounded-full border-2 border-white shadow-sm flex items-center justify-center ${DOT_COLORS[entry.type] || 'bg-gray-400'}`}
            >
              {entry.type === 'original' && <FaStar className="w-2.5 h-2.5 text-white" />}
              {entry.type === 'amendment' && <FaUserEdit className="w-2.5 h-2.5 text-white" />}
              {entry.type === 'pending_approval' && (
                <FaHourglassHalf className="w-2.5 h-2.5 text-white" />
              )}
            </div>

            {/* Content card */}
            <div
              className={`rounded-lg border p-4 transition-colors ${
                entry.type === 'pending_approval'
                  ? 'border-amber-200 bg-amber-50/50'
                  : entry.type === 'original'
                    ? 'border-primary-200 bg-primary-50/30'
                    : 'border-gray-200 bg-surface'
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {entry.version !== null && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 tabular-nums">
                      v{entry.version}
                    </span>
                  )}
                  <span className="text-xs font-medium text-gray-900">
                    {entry.type === 'original'
                      ? 'Original Record'
                      : entry.type === 'pending_approval'
                        ? 'Pending Approval'
                        : 'Amendment'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                  <FaClock className="w-3 h-3" />
                  {formatDate(entry.timestamp)}
                </div>
              </div>

              {/* User & notes — each version's own author (ST-00215) */}
              {entry.user ? (
                <p className="text-xs text-gray-500 mb-2">
                  by <span className="font-medium text-gray-700">{entry.user}</span>
                </p>
              ) : (
                entry.type === 'amendment' && (
                  <p className="text-xs text-gray-400 mb-2">Author not recorded</p>
                )
              )}
              {entry.notes && (
                <p className="text-xs text-gray-600 italic mb-3 bg-gray-50 rounded px-2.5 py-1.5 border border-gray-100">
                  "{entry.notes}"
                </p>
              )}

              {/* Field diffs */}
              {entry.changes.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {entry.changes.map((change, ci) => (
                    <div key={ci} className="flex items-baseline gap-2 text-xs">
                      <span
                        className="w-36 flex-shrink-0 text-gray-500 font-medium truncate"
                        title={change.field}
                      >
                        {change.field}
                      </span>
                      {change.oldValue && (
                        <span
                          className="line-through text-red-400 bg-red-50 px-1.5 py-0.5 rounded text-[11px] truncate max-w-[120px]"
                          title={change.oldValue}
                        >
                          {change.oldValue}
                        </span>
                      )}
                      <span className="text-gray-300">→</span>
                      <span
                        className="text-green-700 bg-green-50 px-1.5 py-0.5 rounded text-[11px] font-medium truncate max-w-[120px]"
                        title={change.newValue || '(blank)'}
                      >
                        {change.newValue || '(blank)'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Fields an archive row written before ST-00215 did not keep: unknown, not changed */}
              {entry.notRecorded && entry.notRecorded.length > 0 && (
                <p className="text-[11px] text-gray-400 mb-3">
                  Not recorded for this version: {entry.notRecorded.join(', ')}
                </p>
              )}

              {/* Inline attachments */}
              {entry.attachments && entry.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-gray-100">
                  {entry.attachments.map((att) => {
                    const fileUrl = api.ticketAttachments.fileUrl(att.StoredFilename);
                    const canPreview = isPreviewable(att.MimeType);
                    return (
                      <div
                        key={att.AttachmentID}
                        className="group relative flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded border border-gray-200 text-[11px] text-gray-600 hover:border-gray-300 transition-colors cursor-pointer"
                        onClick={() =>
                          canPreview && onPreviewAttachment && onPreviewAttachment(att)
                        }
                        title={att.OriginalFilename}
                      >
                        {att.MimeType?.startsWith('image/') ? (
                          <div className="w-5 h-5 rounded overflow-hidden flex-shrink-0">
                            <img src={fileUrl} alt="" className="w-full h-full object-cover" />
                          </div>
                        ) : att.MimeType === 'application/pdf' ? (
                          <FaFilePdf className="w-3 h-3 text-red-400 flex-shrink-0" />
                        ) : (
                          <FaFile className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        )}
                        <span className="truncate max-w-[100px]">{att.OriginalFilename}</span>
                        {canPreview && (
                          <FaSearchPlus className="w-2.5 h-2.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pending approval badge */}
              {entry.type === 'pending_approval' && entry.approval && (
                <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium ring-1 ring-inset ring-amber-300">
                  <FaHourglassHalf className="w-2.5 h-2.5" /> Awaiting approval
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AmendmentTimeline;
